
import { useCallback, useEffect, useRef } from 'react';
import { UserCategory } from '@/types';
import { categorySyncService } from '@/services/categories/categorySyncService';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';

interface UseCategoryLoaderOptions {
  dbInitialized: boolean;
  userId?: string;
  categories: UserCategory[];
  setCategories: (categories: UserCategory[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: any) => void;
  updatePendingCount: () => Promise<void>;
}

export const useCategoryLoader = ({
  dbInitialized,
  userId,
  categories,
  setCategories,
  setIsLoading,
  setError,
  updatePendingCount
}: UseCategoryLoaderOptions) => {
  
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const isFirstPageLoadRef = useRef(true);
  
  // ✅ FIX: Stato più stabile per evitare loop infiniti
  const stableStateRef = useRef({
    lastDbKey: '',
    lastUserKey: '',
    hasInitialized: false,
    lastRun: 0
  });

  // ✅ FIXED: Corretta definizione del metodo loadCategories con 1 parametro
  const loadCategories = useCallback(async (forceReload = false) => {
    const debugInfo = {
      dbInitialized,
      userId: userId?.substring(0, 8),
      isLoading: isLoadingRef.current,
      hasLoaded: hasLoadedRef.current,
      categoriesLength: categories.length,
      forceReload,
      isFirstPageLoad: isFirstPageLoadRef.current
    };
    
    //OfflineLogger.info('useCategoryLoader.loadCategories', { message: 'Loading categories:', debugInfo });

    if (!dbInitialized || !userId) {
      //OfflineLogger.info('useCategoryLoader.loadCategories', 'Database not initialized or no user ID');
      setIsLoading(false);
      return;
    }

    if (isLoadingRef.current) {
      return;
    }

    // ✅ FIX: Controlli più stringenti per evitare reload frequenti
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    
    // ✅ NUOVO: Al primo accesso alla pagina categorie, forza sempre il sync dal server
    if (!forceReload && !isFirstPageLoadRef.current && hasLoadedRef.current && categories.length > 0 && timeSinceLastLoad < 3000) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    lastLoadTimeRef.current = now;

    try {
      //OfflineLogger.info('useCategoryLoader.loadCategories', 'Starting to load categories');
      
      let loadedCategories: UserCategory[];
      
      // ✅ NUOVO: Se è il primo caricamento della pagina e siamo online, forza sync dal server
      if (isFirstPageLoadRef.current && networkService.isOnline()) {
        //OfflineLogger.info('useCategoryLoader.loadCategories', 'First page load - forcing server sync');
        try {
          loadedCategories = await categorySyncService.syncFromServer();
          //OfflineLogger.info('useCategoryLoader.loadCategories', `Synced ${loadedCategories.length} categories from server on first load`);
        } catch (syncError) {
          console.warn('[useCategoryLoader.loadCategories] ⚠️ Server sync failed on first load:', syncError);
          OfflineLogger.error('useCategoryLoader.loadCategories', syncError);
          // Fallback to local categories
          loadedCategories = await categorySyncService.loadLocalCategories();
        }
      } else {
        // Prima prova a caricare dalle cache locale
        loadedCategories = await categorySyncService.loadLocalCategories();
        
        // Se non ci sono categorie locali e siamo online, prova a sincronizzare dal server
        if (loadedCategories.length === 0 && networkService.isOnline() && !hasLoadedRef.current) {
          //OfflineLogger.info('useCategoryLoader.loadCategories', 'No local categories, attempting server sync');
          
          try {
            loadedCategories = await categorySyncService.syncFromServer();
          } catch (syncError) {
            console.warn('[useCategoryLoader.loadCategories] ⚠️ Server sync failed:', syncError);
            OfflineLogger.error('useCategoryLoader.loadCategories', syncError);
          }
        }
      }
      
      //OfflineLogger.info('useCategoryLoader.loadCategories', `Loaded ${loadedCategories.length} categories`);
      
      setCategories(loadedCategories);
      hasLoadedRef.current = true;
      
      if (isFirstPageLoadRef.current) {
        isFirstPageLoadRef.current = false;
      }
      
    } catch (err) {
      console.error('[useCategoryLoader.loadCategories] ❌ Error loading categories:', err);
      OfflineLogger.error('useCategoryLoader.loadCategories', err);
      setError(err);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [dbInitialized, userId, setCategories, setIsLoading, setError, categories.length]);

  const refreshCategories = useCallback(async (forceReload = false) => {
    OfflineLogger.info('useCategoryLoader.refreshCategories', `Refreshing categories (force: ${forceReload})`);

    // ✅ FIX: Solo refresh se forzato o se siamo online senza dati
    const isOnline = networkService.isOnline();
    if (!forceReload && !isOnline && categories.length > 0) {
      OfflineLogger.info('useCategoryLoader.refreshCategories', 'Skipping refresh in offline mode with existing categories');
      return;
    }
    
    // Reset hasLoaded solo se forzato
    if (forceReload) {
      hasLoadedRef.current = false;
    }
    
    await loadCategories(forceReload);
  }, [loadCategories, categories.length]);

  // ✅ FIX: useEffect molto più stabile con controlli anti-loop
  useEffect(() => {
    const now = Date.now();
    const currentDbKey = dbInitialized ? 'initialized' : 'not-initialized';
    const currentUserKey = userId || 'no-user';
    
    // ✅ FIX: Controllo più stringente con timeout per evitare chiamate troppo frequenti
    const timeSinceLastRun = now - stableStateRef.current.lastRun;
    const shouldSkipDueToFrequency = timeSinceLastRun < 500; // 500ms debounce
    
    const debugInfo = {
      currentDbKey,
      currentUserKey,
      previousDbKey: stableStateRef.current.lastDbKey,
      previousUserKey: stableStateRef.current.lastUserKey,
      hasInitialized: stableStateRef.current.hasInitialized,
      isLoading: isLoadingRef.current,
      timeSinceLastRun,
      shouldSkipDueToFrequency,
      isFirstPageLoad: isFirstPageLoadRef.current
    };
    
    //OfflineLogger.info('useCategoryLoader.useEffect', { message: 'Effect triggered:', debugInfo });
    
    if (shouldSkipDueToFrequency) {
      return;
    }
    
    // ✅ FIX: Solo se è veramente una nuova combinazione DB + user o non inizializzato
    const shouldInitialize = dbInitialized && 
                            userId && 
                            !isLoadingRef.current &&
                            (
                              stableStateRef.current.lastDbKey !== currentDbKey ||
                              stableStateRef.current.lastUserKey !== currentUserKey ||
                              !stableStateRef.current.hasInitialized
                            );
    
    if (shouldInitialize) {
      OfflineLogger.info('useCategoryLoader.useEffect', 'Initializing categories load');
      
      // Aggiorna lo stato PRIMA di caricare per evitare loop
      stableStateRef.current = {
        lastDbKey: currentDbKey,
        lastUserKey: currentUserKey,
        hasInitialized: true,
        lastRun: now
      };
      
      hasLoadedRef.current = false; // Reset per nuova sessione
      
      // ✅ FIXED: Call loadCategories with correct parameter
      loadCategories(false).finally(() => {
        updatePendingCount();
      });
    } else if (!dbInitialized || !userId) {
      setIsLoading(false);
    } 
  }, [dbInitialized, userId, loadCategories, updatePendingCount, setIsLoading]);

  // ✅ FIX: Reset solo quando cambia veramente l'utente
  useEffect(() => {
    const currentUserKey = userId || 'no-user';
    const previousUserKey = stableStateRef.current.lastUserKey;
    
    if (previousUserKey && previousUserKey !== 'no-user' && previousUserKey !== currentUserKey) {
      OfflineLogger.info('useCategoryLoader.useEffect', `User changed from ${previousUserKey} to ${currentUserKey} - resetting state`);
      hasLoadedRef.current = false;
      isLoadingRef.current = false;
      lastLoadTimeRef.current = 0;
      stableStateRef.current.hasInitialized = false;
      isFirstPageLoadRef.current = true; // Reset flag per nuovo utente
    }
  }, [userId]);

  return {
    loadCategories,
    refreshCategories
  };
};
